import {
  Address,
  parseAddress,
} from "@certn/cascadia/patterns/address-form/types";
import { useFormikSubmit, useRequest } from "@certn/api";
import { isAddressErrors, useAddressSchema, showToast } from "@certn/cascadia";
import {
  AddressForm,
  ButtonGroup,
  CheckboxFormikField,
  Grid,
  GridItem,
  PrimaryButton,
} from "@certn/cascadia-react";
import { Form, Formik } from "formik";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { useProfile } from "../../auth/ProfileProvider";
import { Schemas } from "../../types";

interface BillingAddressFormCoreProps {
  /**
   * Address to use as default/fallback when checkbox is checked.
   * Can be personal profile address or case address.
   * Uses the shared Address type from cascadia (same as used in Address History/Residences controls).
   * This is the address that will be used if the user selects "Use [default] address".
   */
  defaultAddress?: Address;
  /**
   * Label text for the checkbox (e.g., "Use personal address" or "Use case address").
   */
  checkboxLabel: string;
  /**
   * Current billing address if it exists (source of truth).
   * If this exists, the checkbox will be unchecked by default.
   */
  initialBillingAddress?: Schemas["PersonalInformationBillingAddress"];
  /**
   * Callback called after successful save.
   */
  onSave: () => void;
  /**
   * Test ID prefix for form elements.
   */
  testID?: string;
  /**
   * Custom submit button label.
   */
  submitButtonLabel?: string;
  /**
   * Callback called when the save button enabled state changes.
   * Used to sync the button state with parent components.
   */
  onSaveButtonEnabledChange?: (enabled: boolean) => void;
}

export function BillingAddressFormCore({
  defaultAddress,
  checkboxLabel,
  initialBillingAddress,
  onSave,
  testID = "billing-address-form",
  submitButtonLabel,
  onSaveButtonEnabledChange,
}: BillingAddressFormCoreProps) {
  const { t } = useTranslation();
  const { refresh } = useProfile();
  const addressSchema = useAddressSchema();
  const request = useRequest();

  // Track previous save button enabled state to avoid unnecessary callbacks
  const prevEnabledRef = useRef<boolean | undefined>(undefined);

  const { submit } = useFormikSubmit(
    "/api/internal/applicant/me/personal-info/billing-address/update/",
    {
      onSuccess: async () => {
        await refresh();
        onSave();
      },
      showToastOnError: true,
    }
  );

  // Determine initial address - use billing address if exists, otherwise default address
  const initialAddress = initialBillingAddress || defaultAddress;
  // Checkbox field name - use a generic name that works for both contexts
  const checkboxFieldName = "useDefaultAddress";

  // Store initial checkbox state for comparison
  // - If no defaultAddress: checkbox is always false (unchecked) - form is always editable
  // - If defaultAddress exists and billing address exists: checkbox is false (unchecked) - allow editing
  // - If defaultAddress exists but no billing address: checkbox is true (checked) - use default address
  let initialCheckboxState: boolean;
  if (!defaultAddress) {
    initialCheckboxState = false;
  } else if (initialBillingAddress) {
    initialCheckboxState = false;
  } else {
    initialCheckboxState = true;
  }
  // Parse initial address to ensure consistent format for comparison
  const initialAddressParsed = parseAddress(
    initialAddress ? initialAddress : null
  );

  return (
    <Formik
      initialValues={{
        ...parseAddress(initialAddress ? initialAddress : null),
        [checkboxFieldName]: initialCheckboxState,
      }}
      onSubmit={async (values, helpers) => {
        // If checkbox is checked, call DELETE endpoint to clear billing address
        if (values[checkboxFieldName]) {
          try {
            await request(
              "/api/internal/applicant/me/personal-info/billing-address/",
              {
                method: "DELETE",
              }
            );
            await refresh();
            onSave();
          } catch {
            showToast(t("Something went wrong, try again"), {
              type: "error",
            });
          }
        } else {
          // If checkbox is unchecked, call POST endpoint with address data
          // Exclude checkbox field from the payload
          const addressData = Object.fromEntries(
            Object.entries(values).filter(([key]) => key !== checkboxFieldName)
          ) as Address;
          await submit(addressData, helpers);
        }
      }}
      validationSchema={addressSchema}
      enableReinitialize={true}
    >
      {({ values, setValues, errors, isSubmitting }) => {
        // If checkbox is checked and defaultAddress exists, disable address fields and pre-fill with default address
        // If no defaultAddress, checkbox is always treated as false (unchecked) - form is always editable
        const isAddressDisabled: boolean =
          (defaultAddress && (values[checkboxFieldName] as boolean)) || false;
        const addressToUse = isAddressDisabled ? defaultAddress : values;

        // Determine if save button should be enabled based on changes
        const checkboxChanged = isAddressDisabled !== initialCheckboxState;

        // Compare current address with initial address to detect changes
        // This works for all cases:
        // - When defaultAddress exists and checkbox is checked: addressToUse = defaultAddress, compare with initialAddressParsed
        // - When defaultAddress exists and checkbox is unchecked: addressToUse = values, compare with initialAddressParsed
        // - When defaultAddress is undefined: addressToUse = values, compare with initialAddressParsed (which is initialBillingAddress or empty)
        // Normalize undefined and empty string to empty string for comparison
        const normalizeValue = (val: string | undefined) => val || "";
        const addressChanged =
          !isAddressDisabled &&
          (normalizeValue(addressToUse?.street_address) !==
            normalizeValue(initialAddressParsed.street_address) ||
            normalizeValue(addressToUse?.unit_number) !==
              normalizeValue(initialAddressParsed.unit_number) ||
            normalizeValue(addressToUse?.locality) !==
              normalizeValue(initialAddressParsed.locality) ||
            normalizeValue(addressToUse?.administrative_division_1) !==
              normalizeValue(initialAddressParsed.administrative_division_1) ||
            normalizeValue(addressToUse?.administrative_division_2) !==
              normalizeValue(initialAddressParsed.administrative_division_2) ||
            normalizeValue(addressToUse?.country) !==
              normalizeValue(initialAddressParsed.country) ||
            normalizeValue(addressToUse?.postal_code) !==
              normalizeValue(initialAddressParsed.postal_code));

        const shouldEnableSaveButton = checkboxChanged || addressChanged;

        // Notify parent of save button state changes (only when value changes)
        if (prevEnabledRef.current !== shouldEnableSaveButton) {
          prevEnabledRef.current = shouldEnableSaveButton;
          onSaveButtonEnabledChange?.(shouldEnableSaveButton);
        }

        return (
          <Form>
            <Grid width="100%" form>
              {defaultAddress && (
                <GridItem>
                  <CheckboxFormikField
                    name={checkboxFieldName}
                    testID={`${testID}-checkbox`}
                  >
                    {checkboxLabel}
                  </CheckboxFormikField>
                </GridItem>
              )}
              <AddressForm
                value={addressToUse || {}}
                onChange={(value) => {
                  if (!isAddressDisabled) {
                    setValues({ ...values, ...value });
                  }
                }}
                errors={isAddressErrors(errors) ? errors : undefined}
                testID={testID}
                isDisabled={isAddressDisabled}
              />
              <GridItem>
                <ButtonGroup>
                  <PrimaryButton
                    type="submit"
                    size="medium"
                    testID={`${testID}-save-button`}
                    isLoading={isSubmitting}
                    isDisabled={!shouldEnableSaveButton}
                  >
                    {submitButtonLabel || t("Save")}
                  </PrimaryButton>
                </ButtonGroup>
              </GridItem>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
}
